// ─── leads.form_type → is this row somebody entering the hiring pipeline? ──────
//
// WHY THIS EXISTS
// The Leads list presented every public.leads row identically: one indigo pill,
// and — far more expensively — one "needs attention" predicate that asked only
// "does a candidate match this by email or phone?".
//
// A review submission, a pay-calculator capture, a scholarship application, a
// job-alert signup or a facility staffing request will NEVER match a candidate,
// because none of them is a candidate. So each one was permanently unmatched: it
// inflated the amber Leads nav badge, sat in the default view forever, and
// offered a one-click Promote into the candidate pipeline.
//
// The worker already refuses these. recruiter-agent/src/store.js:231
// CANDIDATE_FORM_TYPES is a whitelist; :288 isCandidateLead() gates outreach on
// it, and anything outside is escalated to a human instead of texted. The CRM
// admitted them one screen later. recruiter-agent/tests/outreach.test.js:105
// says why that matters in as many words:
//
//   "admitting them would count review submitters and calculator users as
//    inbound candidates and corrupt CRM attribution and cost-per-hire math."
//
// MIRRORED, NOT REINVENTED. This is deliberately a copy of the worker's
// whitelist rather than a CRM-side blacklist of the known-bad types, so that
// admitting a new candidate form stays ONE decision made in one place. If the
// two ever drift, the drift is a whitelist that is too NARROW here — which
// under-counts attention, and is visible — rather than one too wide, which
// silently corrupts attribution.
//
// WHAT ENFORCES THAT, precisely. Until 2026-09-09 this comment ended "The test
// pins the membership list against the worker's", which was not true.
// ./leadClassification.test.js pins it against WORKER_CANDIDATE_FORM_TYPES — a
// literal array typed into that test file. Both copies live in this repo, so a
// form type added to the worker would leave every test here green and the two
// repos silently out of step. It pinned this list against a TRANSCRIPTION of the
// worker, which is a different and much weaker claim.
//
// ./crossRepoDrift.test.js is the one that reads the worker itself: the sibling
// checkout when one is on disk, and otherwise ./recruiterAgentFormTypes.snapshot.json,
// refreshed by `npm run sync:form-types -- --write` and cross-checked against the
// sibling whenever both are present. The transcription test still earns its place
// — it documents intent readably — but it is not the drift guard.
//
// PURE ON PURPOSE, same reason as ./messageStatus: this repo has jest via
// react-scripts but no @testing-library, so a pure function is the only thing
// that can actually be pinned here.

/**
 * Mirrors recruiter-agent/src/store.js:231 CANDIDATE_FORM_TYPES exactly.
 * A lead outside this set is a tool capture, an enquiry, or a facility — not
 * somebody entering the hiring pipeline.
 */
export const CANDIDATE_FORM_TYPES = new Set([
  'profile',            // Submit Profile
  'therapist_lead',     // For Therapists apply modal
  'job_apply',          // job application (home + /jobs)
  'contact',            // general contact (home + health-insurance-options)
  'meta_therapist_lead',// Meta Instant Form (lead ads), 2026-08-13
  'manual',             // the CRM's own + New candidate, 2026-10-06 (hub)
  'meta_lead',          // the new candidate Instant Form, 2026-10-05 (hub); meta_lead_dark stays out
  'chat',               // the flagship chat widget's lead form, 2026-10-17 (NIGHT-AGENTS §4); consent still decides texting
  // The satellite network's "Get Matched" forms, admitted 2026-09-03. NOT a
  // blanket blessing: three satellite forms post form_type=lead while being a
  // review submission or a calculator capture, and those are being split onto
  // their own form_types on the satellites' `gated/form-type-split` branches.
  // Until those are live, such rows arrive as `lead` and count here.
  'lead',
  // NIGHT-AGENTS-2026-10-25c "For DIRECTSTAFF": home's /next-contract form (form_type next_contract, "actively looking"). The agents
  // asked the CRM to add it FIRST, then they added it on the worker side (recruiter-agent 943509c, 2026-10-26).
  'next_contract',
]);
/** Types the CRM admits BEFORE the worker, by the agents' own request (named in their report). crossRepoDrift.test.js allows exactly
 *  these as "only in the CRM" -- and fails once the worker has one, so this list is emptied the moment it has done its job. */
export const CRM_LEADS_WORKER = new Set([]);   // empty: next_contract is on both sides since recruiter-agent 943509c

/**
 * True only for a lead whose form_type means a candidate. Unknown, empty and
 * missing form_types are NOT candidates — the same fail-closed direction the
 * worker takes, where an unrecognised type is escalated to a human rather than
 * texted. Here the consequence is milder: the row is still listed, still
 * promotable, just not counted as needing attention.
 */
export function isCandidateLead(lead) {
  const ft = (lead && lead.form_type ? String(lead.form_type) : '').trim().toLowerCase();
  return CANDIDATE_FORM_TYPES.has(ft);
}

// ─── …and the second question: must a HUMAN pick this row up? ─────────────────
//
// These are not the same question, and collapsing them is what broke the badge.
//
// `meta_lead_dark` is the worker's deliberate label for a Meta lead that arrived
// WITHOUT usable SMS consent: recruiter-agent refuses to text it and escalates it
// to a person instead. It is correctly outside CANDIDATE_FORM_TYPES — that set
// gates OUTREACH, and this row must never be texted — but it is a paid lead that
// somebody has to phone. Excluding it from "needs attention" hid exactly the row
// with the highest cost of being missed.
//
// So the whitelist splits in two, and the direction of each is the safe one:
//   isCandidateLead  — narrow. Gates anything that implies texting or counts
//                      toward candidate attribution. Unchanged, byte-for-byte.
//   isNeedsHuman     — wider by exactly one. Gates the amber badge and the
//                      Leads default view: who must a recruiter call today.
//
// Widening the WORK queue is cheap and self-correcting: a recruiter sees a row
// that did not need them and moves on. Widening the OUTREACH gate is not — it
// texts somebody who never consented. Only isNeedsHuman may grow here.

/**
 * isCandidateLead's set plus the types that a human must action but the agent
 * must never text. Derived from CANDIDATE_FORM_TYPES rather than retyped, so
 * admitting a new candidate form stays the one decision it was.
 */
export const NEEDS_HUMAN_FORM_TYPES = new Set([
  ...CANDIDATE_FORM_TYPES,
  // Meta lead ads, no SMS consent captured. Worker escalates instead of texting;
  // the CRM is where that escalation is supposed to become a phone call.
  'meta_lead_dark',
  // A facility asking for staff. recruiter-agent 94283f8 (2026-09-08) added this to
  // PAGE_BEFORE_GATE_FORM_TYPES: the worker now texts a recruiter, at urgent
  // priority, BEFORE the consent gate, for every one of these -- because a facility
  // asking for staff is a person waiting for a callback.
  //
  // Without it here, that lead paged a phone at 2am and was then EXCLUDED from the
  // Leads default view the recruiter opens to work it, shown grey with the title
  // "Not a candidate form type". Identical to the defect 22c799c fixed for
  // meta_lead_dark, and it recurred within a day because the two lists live in two
  // repos and only one moved.
  //
  // src/lib/crossRepoDrift.test.js now reads the worker's own file and compares, so
  // the next divergence fails a test instead of hiding in a comment.
  'facility_staffing_request',
  // ONE FACILITY CODE (hub ruling 2026-10-04): new facility leads arrive as 'facility_request' (the leads worker
  // aliases the form's old spelling); older rows keep 'facility_staffing_request'. Both are the same person waiting.
  'facility_request',
]);

/**
 * True for a lead a person should pick up. Fails closed the same way
 * isCandidateLead does — unknown, empty and missing form_types are excluded, so
 * an unrecognised type goes uncounted rather than inflating the badge again.
 */
export function isNeedsHuman(lead) {
  const ft = (lead && lead.form_type ? String(lead.form_type) : '').trim().toLowerCase();
  return NEEDS_HUMAN_FORM_TYPES.has(ft);
}

const CALL_ONLY_TITLES = {
  meta_lead_dark: 'Meta lead — no SMS consent, call them',
  facility_staffing_request: 'Facility needs staff — call them back',
  facility_request: 'Facility needs staff — call them back',
};

/**
 * The Form column's pill, in three states. Candidates keep today's indigo
 * byte-for-byte; a needs-human-but-not-candidate type (today: meta_lead_dark)
 * is amber, matching the nav badge it now feeds; everything else stays grey.
 *
 * Amber rather than grey is the whole point of the fix being visible: grey reads
 * as "ignore me", and this is the row a recruiter is losing money by ignoring.
 *
 * The form_type text itself is ALWAYS the label — recolouring it must not hide
 * which form produced the row, since that is the one field that explains the
 * colour. A missing form_type shows the existing em dash.
 */
export function formTypePillFor(lead) {
  const raw = (lead && lead.form_type ? String(lead.form_type) : '').trim();
  const candidate = isCandidateLead(lead);
  const needsHuman = isNeedsHuman(lead);
  const callOnly = needsHuman && !candidate;

  let style, title;
  if (candidate) {
    style = { background: '#eef2ff', color: '#4338ca' };
    title = 'Candidate form type — counts toward "needs attention"';
  } else if (callOnly) {
    style = { background: '#fef3c7', color: '#92400e' };
    // Keyed, not hardcoded to the one member: if NEEDS_HUMAN_FORM_TYPES ever
    // grows again, a wrong explanation is worse than a generic one.
    title = CALL_ONLY_TITLES[raw.toLowerCase()]
      || 'Needs a human — the agent will not text this lead';
  } else {
    style = { background: '#f1f5f9', color: '#475569' };
    title = 'Not a candidate form type — excluded from "needs attention"';
  }

  return { label: raw || '—', isCandidate: candidate, needsHuman, style, title };
}
